// src/components/AuthorizeDialog.tsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  Box,
  Grid,
  Divider,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

import cloud from './cloud.png';
import serial from './serial.png';

interface Props {
  open: boolean;
  onClose: () => void;
}

const AuthorizeDialog = ({ open, onClose }: Props) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isXs}
      fullWidth
      maxWidth={isXs ? 'xs' : 'md'}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle
        sx={{
          pr: 6,
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Typography
          variant={isXs ? 'subtitle1' : 'h6'}
          fontWeight="bold"
          sx={{ my: 0 }}
        >
          How to Authorize Your Plugins or Subscription
        </Typography>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 10, sm: 3 }, // room for sticky footer on mobile
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 3 },
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            mx: 'auto',
            color: '#222',
            fontSize: { xs: '0.95rem', sm: '1rem' },
            lineHeight: { xs: 1.7, sm: 1.8 },
            maxWidth: 1000,
          }}
        >
          <Grid container spacing={{ xs: 2, sm: 4 }}>
            <Grid item xs={12} md={6}>
              <Typography fontWeight="bold" sx={{ mb: 1 }}>
                Authorize with Your Serial Number
              </Typography>
              <Typography>
                1 – Find your serial on your invoice or sales confirmation
                email.
              </Typography>
              <Typography>
                2 – Enter it into the dialog with your name. (Organization is
                optional.)
              </Typography>
              <Typography>
                3 – Enter the serial exactly as shown, including dashes.
              </Typography>
              <Typography>4 – Click OK.</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography fontWeight="bold" sx={{ mb: 1 }}>
                Authorize with Your Anarchy Cloud Login
              </Typography>
              <Typography>
                1 – Enter your email address into the <b>Name</b> field.
              </Typography>
              <Typography>
                2 – The dialog will reveal the Password field.
              </Typography>
              <Typography>3 – Enter your password.</Typography>
              <Typography>4 – Click OK.</Typography>
            </Grid>
          </Grid>

          {/* Image row */}
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: { xs: 3, sm: 4 } }}
          >
            <Grid item xs={12} sm={6} display="flex" justifyContent="center">
              <Box
                component="img"
                src={serial}
                alt="Serial Number"
                sx={{
                  width: { xs: '100%', sm: '100%' },
                  maxWidth: { xs: 360, sm: 480 },
                  height: 'auto',
                  borderRadius: 1,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} display="flex" justifyContent="center">
              <Box
                component="img"
                src={cloud}
                alt="Cloud Login"
                sx={{
                  width: { xs: '100%', sm: '100%' },
                  maxWidth: { xs: 360, sm: 480 },
                  height: 'auto',
                  borderRadius: 1,
                }}
              />
            </Grid>
          </Grid>

          <Typography align="center" sx={{ my: { xs: 2.5, sm: 3 } }}>
            Still have questions?{' '}
            <Box
              component="a"
              href="https://www.digitalanarchy.com/support/install.html"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#1976D2', fontWeight: 700 }}
            >
              Watch a video tutorial
            </Box>{' '}
            for detailed instructions.
          </Typography>
        </Box>
      </DialogContent>

      {/* Sticky footer on mobile */}
      <DialogActions
        sx={{
          position: { xs: 'sticky', sm: 'static' },
          bottom: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Button onClick={onClose} variant="contained" fullWidth={isXs}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthorizeDialog;
